import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ErrorState } from '@xing-com/empty-state';
import { IconTick } from '@xing-com/icons';
import { Headline } from '@xing-com/typography';

import { MetaData } from '../../components/meta-data';
import { useCompaniesExperiments } from '../../hooks/use-companies-experiments/use-companies-experiments';
import { useTrackPage } from '../../hooks/use-track-page';
import { pagesTrackingName } from '../../utils/tracking';
import { CompaniesIndustriesModule } from './companies-industries-module';
import * as Styled from './logged-out-landing-page.style';
import { MostVisitedCompaniesModule } from './most-visited-companies-module';
import { NewLoggedOutLandingPage } from './new-logged-out-landing-page';
import { RecentlyClaimedCompaniesModule } from './recently-claimed-companies-module';

const TickListItem = ({ id }: { id: string }) => (
  <Styled.TickListItem>
    <IconTick width={24} height={24} />
    <Styled.TickListItemContent>
      <FormattedMessage
        id={id}
        values={{
          strong: (...text) => <strong>{text}</strong>,
        }}
      />
    </Styled.TickListItemContent>
  </Styled.TickListItem>
);

const LoggedOutDefault = ({
  setError,
}: {
  setError: Dispatch<SetStateAction<boolean>>;
}) => {
  const intl = useIntl();
  return (
    <>
      <MetaData
        index
        title={intl.formatMessage({ id: 'CP_100_META_TITLE_HUBPAGE' })}
        description={intl.formatMessage({
          id: 'CP_LANDING_LOGGED_OUT_HEADLINE',
        })}
      />
      <Styled.Header>
        <Headline as="h1" size="xxlarge">
          <FormattedMessage id="CP_LANDING_LOGGED_OUT_HEADLINE" />
        </Headline>
        <Styled.InfoWrapper>
          <FormattedMessage id="CP_100_LOGGEDOUT_EXISTING_PROFILES_TEXT_NEW" />
        </Styled.InfoWrapper>
        <Styled.TickListItemsWrapper>
          <TickListItem id="CP_LANDING_LOGGED_OUT_INFORMATION_AND_UPDATES" />
          <TickListItem id="CP_LANDING_LOGGED_OUT_EMPLOYEE_REVIEWS" />
          <TickListItem id="CP_LANDING_LOGGED_OUT_INTRO_CAREER_OPORTUNITIES" />
        </Styled.TickListItemsWrapper>
      </Styled.Header>
      <CompaniesIndustriesModule setError={setError} />
      <RecentlyClaimedCompaniesModule setError={setError} />
      <MostVisitedCompaniesModule setError={setError} />
    </>
  );
};

const LoggedOutLandingPage: React.FC = () => {
  const [error, setError] = useState<boolean>(false);
  const { isNewLoggedOutExperimentEnabled } = useCompaniesExperiments();

  const LandingPage = isNewLoggedOutExperimentEnabled
    ? NewLoggedOutLandingPage
    : LoggedOutDefault;

  useTrackPage({ pageName: pagesTrackingName.loggedOutLanding });
  if (error) {
    return (
      <Styled.ErrorPageWrapper>
        <ErrorState />
      </Styled.ErrorPageWrapper>
    );
  }
  return <LandingPage setError={setError} />;
};

export { LoggedOutLandingPage };
